import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { ArrowLeft, Archive, Info, Loader2, Rocket, Send, Users } from 'lucide-react'
import apiClient from '@/api/client'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { Switch } from '@/components/ui/switch'
import { useToast } from '@/hooks/use-toast'
import { describeApiError } from '@/lib/apiError'
import { cn } from '@/lib/utils'
import type { KitOffer, KitUpdateInput, MarketingKit } from '@/types/marketingProactive'
import { MarketingShell } from '../MarketingShell'
import { dateRange, KIND_LABEL } from './proactiveMeta'
import { KitWords } from './KitWords'
import { KitVisuals } from './KitVisuals'
import { KitResults } from './KitResults'
import { DishPicker } from './DishPicker'

const STATIC_SEGMENTS: { key: string; label: string }[] = [
  { key: 'regulars', label: 'Regulars' },
  { key: 'vips', label: 'VIPs' },
  { key: 'at_risk', label: 'At risk' },
  { key: 'lapsed_60d', label: 'Lapsed (60+ days)' },
  { key: 'first_timers_recent', label: 'First-timers (last 30 days)' },
  { key: 'birthdays_this_month', label: 'Birthdays this month' },
]

/**
 * One kit, end to end (F-MKT-N1): offer, dishes, audience and words on the
 * left; the visuals and the launch / results on the right. Launching creates
 * the code and a draft campaign; sending happens in Campaigns.
 */
export function KitBuilder({ kitId }: { kitId: string }) {
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const query = useQuery({
    queryKey: ['marketing', 'kit', kitId],
    queryFn: () => apiClient.getMarketingKit(kitId),
  })
  const kit = query.data?.data

  const update = useMutation({
    mutationFn: (input: KitUpdateInput) => apiClient.updateMarketingKit(kitId, input),
    onSuccess: (res) => {
      queryClient.setQueryData(['marketing', 'kit', kitId], res)
      queryClient.invalidateQueries({ queryKey: ['marketing', 'kits'] })
    },
    onError: (err) => toast({ title: 'Could not save', description: describeApiError(err).message, variant: 'destructive' }),
  })

  if (query.isError) {
    return (
      <MarketingShell title="Kit">
        <Card>
          <CardContent className="space-y-2 p-6 text-sm">
            <p>This kit could not be loaded.</p>
            <Link to="/admin/marketing/kits" className="font-medium text-primary hover:underline">
              Back to all kits
            </Link>
          </CardContent>
        </Card>
      </MarketingShell>
    )
  }
  if (!kit) {
    return (
      <MarketingShell title="Kit">
        <div className="grid gap-4 lg:grid-cols-2">
          <Skeleton className="h-96 rounded-lg" />
          <Skeleton className="h-96 rounded-lg" />
        </div>
      </MarketingShell>
    )
  }

  const draft = kit.status === 'draft'
  const save = (input: KitUpdateInput, msg?: string) =>
    update.mutate(input, { onSuccess: () => msg && toast({ title: msg }) })

  return (
    <MarketingShell
      title={kit.title}
      description={`${KIND_LABEL[kit.kind]} kit · ${kit.when}`}
      actions={<KitHeaderActions kit={kit} />}
    >
      <div className="space-y-4">
        <Link to="/admin/marketing/kits" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
          <ArrowLeft className="h-4 w-4" />
          All kits
        </Link>

        {kit.notes.length > 0 && (
          <div className="space-y-1 rounded-lg border bg-muted/30 px-4 py-3">
            {kit.notes.map((n) => (
              <p key={n} className="flex gap-2 text-sm text-muted-foreground">
                <Info className="mt-0.5 h-4 w-4 shrink-0" />
                {n}
              </p>
            ))}
          </div>
        )}

        <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
          <div className="space-y-4">
            <OfferCard kit={kit} editable={draft} saving={update.isPending} onSave={(input) => save(input, 'Offer saved')} />
            <DishesCard kit={kit} editable={draft} onSave={(ids) => save({ product_ids: ids }, 'Dishes saved')} />
            <AudienceCard kit={kit} editable={draft} onSave={(seg) => save({ segment_def: seg }, 'Audience saved')} />
            <KitWords kit={kit} />
          </div>
          <div className="space-y-4 lg:sticky lg:top-4 lg:self-start">
            <KitVisuals kit={kit} />
            {draft ? <LaunchCard kit={kit} /> : <LiveCard kit={kit} />}
            {!draft && <KitResults kitId={kit.id} />}
          </div>
        </div>
      </div>
    </MarketingShell>
  )
}

function KitHeaderActions({ kit }: { kit: MarketingKit }) {
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const archive = useMutation({
    mutationFn: () => apiClient.archiveMarketingKit(kit.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['marketing'] })
      toast({ title: 'Kit archived' })
    },
    onError: () => toast({ title: 'Could not archive', variant: 'destructive' }),
  })
  return (
    <div className="flex items-center gap-2">
      <Badge variant={kit.status === 'launched' ? 'default' : 'secondary'}>
        {kit.status === 'draft' ? 'Draft' : kit.status === 'launched' ? 'Launched' : 'Archived'}
      </Badge>
      {kit.status !== 'archived' && (
        <Button variant="ghost" size="sm" disabled={archive.isPending} onClick={() => archive.mutate()}>
          <Archive className="mr-1.5 h-4 w-4" />
          Archive
        </Button>
      )}
    </div>
  )
}

function OfferCard({
  kit,
  editable,
  saving,
  onSave,
}: {
  kit: MarketingKit
  editable: boolean
  saving: boolean
  onSave: (input: KitUpdateInput) => void
}) {
  const [offer, setOffer] = useState<KitOffer>(kit.offer)
  const [starts, setStarts] = useState(kit.starts_on)
  const [ends, setEnds] = useState(kit.ends_on)
  useEffect(() => {
    setOffer(kit.offer)
    setStarts(kit.starts_on)
    setEnds(kit.ends_on)
  }, [kit.offer, kit.starts_on, kit.ends_on])

  const dirty = JSON.stringify(offer) !== JSON.stringify(kit.offer) || starts !== kit.starts_on || ends !== kit.ends_on
  const active = offer.type !== 'none'
  const codeOk = !active || /^[A-Z0-9_-]{3,40}$/.test(offer.code.trim().toUpperCase())
  const valueOk = !active || (offer.value > 0 && (offer.type !== 'percent' || offer.value <= 100))
  const datesOk = Boolean(starts) && Boolean(ends) && ends >= starts

  if (!editable) {
    return (
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Offer</CardTitle>
        </CardHeader>
        <CardContent className="space-y-1 text-sm">
          <p>{kit.offer_label || 'No offer — posts and poster only'}</p>
          {kit.promo_code && (
            <p>
              Code <span className="font-mono font-medium">{kit.promo_code.code}</span>
              {kit.offer.per_guest ? ' (each guest gets their own)' : ''}
            </p>
          )}
          <p className="text-muted-foreground">{dateRange(kit.starts_on, kit.ends_on)}</p>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">Offer</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label>Type</Label>
            <Select
              value={offer.type}
              onValueChange={(v) =>
                setOffer({ ...offer, type: v as KitOffer['type'], value: v === 'none' ? 0 : offer.value || (v === 'percent' ? 10 : 200) })
              }
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No offer</SelectItem>
                <SelectItem value="percent">% off the bill</SelectItem>
                <SelectItem value="flat">Amount off the bill</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {active && (
            <div className="space-y-1.5">
              <Label htmlFor="offer-value">{offer.type === 'percent' ? 'Percent off' : 'Amount off (Rs)'}</Label>
              <Input
                id="offer-value"
                type="number"
                min={1}
                max={offer.type === 'percent' ? 100 : undefined}
                value={offer.value || ''}
                onChange={(e) => setOffer({ ...offer, value: Number(e.target.value) })}
              />
            </div>
          )}
        </div>
        {active && (
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="offer-code">Code</Label>
              <Input
                id="offer-code"
                className="font-mono uppercase"
                value={offer.code}
                onChange={(e) => setOffer({ ...offer, code: e.target.value.toUpperCase() })}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="offer-min">Minimum bill (optional)</Label>
              <Input
                id="offer-min"
                type="number"
                min={0}
                value={offer.min_spend || ''}
                onChange={(e) => setOffer({ ...offer, min_spend: Number(e.target.value) })}
              />
            </div>
          </div>
        )}
        {active && (
          <div className="flex items-center justify-between gap-3">
            <div>
              <p className="text-sm font-medium">A personal code for each guest</p>
              <p className="text-xs text-muted-foreground">Each guest gets a one-time code only they can use.</p>
            </div>
            <Switch checked={offer.per_guest} onCheckedChange={(v) => setOffer({ ...offer, per_guest: v })} />
          </div>
        )}
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="kit-starts">Runs from</Label>
            <Input id="kit-starts" type="date" value={starts} onChange={(e) => setStarts(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="kit-ends">Until</Label>
            <Input id="kit-ends" type="date" min={starts} value={ends} onChange={(e) => setEnds(e.target.value)} />
          </div>
        </div>
        {dirty && (
          <div className="flex justify-end gap-2">
            <Button
              variant="ghost"
              onClick={() => {
                setOffer(kit.offer)
                setStarts(kit.starts_on)
                setEnds(kit.ends_on)
              }}
            >
              Undo
            </Button>
            <Button
              disabled={saving || !codeOk || !valueOk || !datesOk}
              onClick={() => onSave({ offer: { ...offer, code: offer.code.trim().toUpperCase() }, starts_on: starts, ends_on: ends })}
            >
              Save offer
            </Button>
          </div>
        )}
        {!codeOk && <p className="text-xs text-destructive">Codes are 3–40 letters, numbers, dashes or underscores.</p>}
      </CardContent>
    </Card>
  )
}

function DishesCard({ kit, editable, onSave }: { kit: MarketingKit; editable: boolean; onSave: (ids: string[]) => void }) {
  const [picking, setPicking] = useState(false)
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-base">Featured dishes</CardTitle>
        {editable && (
          <Button size="sm" variant="outline" onClick={() => setPicking(true)}>
            Change
          </Button>
        )}
      </CardHeader>
      <CardContent>
        {kit.products.length === 0 ? (
          <p className="text-sm text-muted-foreground">No dishes picked — the visuals use type only.</p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {kit.products.map((p) => (
              <div key={p.id} className="flex items-center gap-2 rounded-md border p-1.5 pr-3">
                {p.image_url ? (
                  <img src={p.image_url} alt="" className="h-9 w-9 rounded object-cover" />
                ) : (
                  <div className="h-9 w-9 rounded bg-muted" />
                )}
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium">{p.name}</p>
                  <p className="text-xs text-muted-foreground">Rs {Math.round(p.price).toLocaleString('en-US')}</p>
                </div>
              </div>
            ))}
          </div>
        )}
        {kit.products.length > 0 && kit.products.every((p) => !p.image_url) && (
          <p className="mt-2 text-xs text-muted-foreground">
            None of these have a photo. Add one in Menu and the posts will use it.
          </p>
        )}
      </CardContent>
      <DishPicker
        open={picking}
        onOpenChange={setPicking}
        selected={kit.product_ids}
        onSave={(ids) => {
          onSave(ids)
          setPicking(false)
        }}
      />
    </Card>
  )
}

function AudienceCard({
  kit,
  editable,
  onSave,
}: {
  kit: MarketingKit
  editable: boolean
  onSave: (seg: Record<string, unknown>) => void
}) {
  const current = (kit.segment_def?.segment as string | undefined) ?? 'none'
  // The kit's own audience (dish buyers, milestone guests) stays selectable
  // after switching away, so switching back restores its ids.
  const kitOwn = useMemo(() => {
    if (current === 'product_buyers' || current === 'customer_list') return kit.segment_def
    return null
  }, [kit.id]) // eslint-disable-line react-hooks/exhaustive-deps
  const canBuyers = kit.product_ids.length > 0

  const choose = (v: string) => {
    if (v === 'none') return onSave({})
    if (kitOwn && v === (kitOwn.segment as string)) return onSave(kitOwn)
    if (v === 'product_buyers') {
      return onSave({ segment: 'product_buyers', product_ids: kit.product_ids, days: 120, label: 'Guests who ordered the featured dishes' })
    }
    onSave({ segment: v })
  }

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">Who hears about it</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {editable ? (
          <Select value={current} onValueChange={choose}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {kitOwn && (
                <SelectItem value={kitOwn.segment as string}>{(kitOwn.label as string) || 'This kit\'s guests'}</SelectItem>
              )}
              {canBuyers && current !== 'product_buyers' && !kitOwn && (
                <SelectItem value="product_buyers">Guests who ordered the featured dishes</SelectItem>
              )}
              {STATIC_SEGMENTS.map((s) => (
                <SelectItem key={s.key} value={s.key}>
                  {s.label}
                </SelectItem>
              ))}
              <SelectItem value="none">Nobody — posts and poster only</SelectItem>
            </SelectContent>
          </Select>
        ) : (
          <p className="text-sm font-medium">{kit.audience.label}</p>
        )}
        {kit.audience.has_target && (
          <p className="flex items-center gap-2 text-sm text-muted-foreground">
            <Users className="h-4 w-4" />
            {kit.audience.count.toLocaleString('en-US')} guests · {kit.audience.whatsapp.toLocaleString('en-US')} on WhatsApp ·{' '}
            {kit.audience.email.toLocaleString('en-US')} by email
          </p>
        )}
      </CardContent>
    </Card>
  )
}

function LaunchCard({ kit }: { kit: MarketingKit }) {
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const [confirm, setConfirm] = useState(false)
  const launch = useMutation({
    mutationFn: () => apiClient.launchMarketingKit(kit.id),
    onSuccess: (res) => {
      queryClient.setQueryData(['marketing', 'kit', kit.id], res)
      queryClient.invalidateQueries({ queryKey: ['marketing'] })
      queryClient.invalidateQueries({ queryKey: ['campaigns'] })
      setConfirm(false)
      toast({ title: 'Kit launched', description: res.data?.promo_code ? `Code ${res.data.promo_code.code} is live.` : undefined })
    },
    onError: (err) => {
      setConfirm(false)
      toast({ title: 'Could not launch', description: describeApiError(err).message, variant: 'destructive' })
    },
  })
  const hasOffer = kit.offer.type !== 'none'
  const steps = [
    hasOffer ? `Creates code ${kit.offer.code} (${kit.offer_label}), valid ${dateRange(kit.starts_on, kit.ends_on)}.` : null,
    kit.audience.has_target
      ? `Creates a draft campaign to ${kit.audience.label.toLowerCase()} (${kit.audience.count.toLocaleString('en-US')} guests). Nothing is sent until you press Send in Campaigns.`
      : 'No campaign — use the posts and poster.',
  ].filter(Boolean) as string[]

  return (
    <Card className="border-primary/30">
      <CardHeader className="pb-2">
        <CardTitle className="text-base">Launch</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <ul className="space-y-1 text-sm text-muted-foreground">
          {steps.map((s) => (
            <li key={s}>• {s}</li>
          ))}
        </ul>
        <Button className="w-full" onClick={() => setConfirm(true)}>
          <Rocket className="mr-2 h-4 w-4" />
          Launch kit
        </Button>
      </CardContent>
      <Dialog open={confirm} onOpenChange={setConfirm}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Launch {kit.title}?</DialogTitle>
            <DialogDescription>After launch the offer, dates, dishes and audience are locked. The words stay editable.</DialogDescription>
          </DialogHeader>
          <ul className="space-y-1 text-sm">
            {steps.map((s) => (
              <li key={s}>• {s}</li>
            ))}
          </ul>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setConfirm(false)}>
              Cancel
            </Button>
            <Button disabled={launch.isPending} onClick={() => launch.mutate()}>
              {launch.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Launch
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  )
}

function LiveCard({ kit }: { kit: MarketingKit }) {
  const sent = Boolean(kit.campaign?.sent_at)
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">Live</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        {kit.promo_code && (
          <p>
            Code <span className="font-mono font-medium">{kit.promo_code.code}</span> works at the counter and online,{' '}
            {dateRange(kit.starts_on, kit.ends_on)}.
          </p>
        )}
        {kit.campaign ? (
          <div className={cn('flex flex-col gap-2 rounded-md border p-3 sm:flex-row sm:items-center sm:justify-between')}>
            <p className="text-muted-foreground">
              Campaign “{kit.campaign.name}” · {sent ? 'sent' : 'draft, not sent yet'}
            </p>
            <Button size="sm" variant={sent ? 'outline' : 'default'} asChild>
              <Link to="/admin/marketing/campaigns" search={{ open: kit.campaign.id }}>
                <Send className="mr-1.5 h-4 w-4" />
                {sent ? 'Open campaign' : 'Review & send'}
              </Link>
            </Button>
          </div>
        ) : (
          <p className="text-muted-foreground">No campaign for this kit — share the posts and print the poster.</p>
        )}
      </CardContent>
    </Card>
  )
}
